import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Filter, ChevronDown, ChevronRight, Eye, Users, FileText, ExternalLink, Pencil, Send } from "lucide-react";
import { toast } from "sonner";

const itDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");

export default function ProspectFunnelConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [iscritti, setIscritti] = useState({});
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState("");
  const [preview, setPreview] = useState(null);
  const [iscrittiDlg, setIscrittiDlg] = useState(null); // fid
  const [contenutoDlg, setContenutoDlg] = useState(null); // fid

  const load = () => api.get("/prospect-funnel-config").then((r) => setCfg(r.data)).catch(() => setCfg(false));
  const loadIscritti = () => api.get("/prospect-funnel/iscritti").then((r) => setIscritti(r.data)).catch(() => {});
  useEffect(() => { load(); loadIscritti(); }, []); // eslint-disable-line
  useEffect(() => {
    const h = () => loadIscritti();
    window.addEventListener("prospect-funnel-updated", h);
    return () => window.removeEventListener("prospect-funnel-updated", h);
  }, []);

  if (cfg === null) return <Card className="p-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></Card>;
  if (cfg === false || !cfg.can_edit) return null;

  const setStep = (fid, si, patch) => setCfg((s) => {
    const f = { ...s.funnels };
    const steps = f[fid].steps.map((st, i) => (i === si ? { ...st, ...patch } : st));
    f[fid] = { ...f[fid], steps };
    return { ...s, funnels: f };
  });
  const setFunnel = (fid, patch) => setCfg((s) => ({ ...s, funnels: { ...s.funnels, [fid]: { ...s.funnels[fid], ...patch } } }));
  const setLink = (fid, lab, url) => setCfg((s) => ({ ...s, funnels: { ...s.funnels, [fid]: { ...s.funnels[fid], links: { ...(s.funnels[fid].links || {}), [lab]: url } } } }));

  const save = async () => {
    setSaving(true);
    try {
      const funnels = {};
      (cfg.order || []).forEach((fid) => {
        const f = cfg.funnels[fid];
        const steps = {};
        f.steps.forEach((st, i) => { steps[i] = { subject: st.subject, testo: st.testo }; });
        funnels[fid] = { attivo: f.attivo, steps, links: f.links || {} };
      });
      const r = await api.put("/prospect-funnel-config", { attivo_globale: cfg.attivo_globale, cc_agente: cfg.cc_agente !== false, cc_extra_enabled: !!cfg.cc_extra_enabled, cc_extra: cfg.cc_extra || "", funnels });
      setCfg((s) => ({ ...r.data, can_edit: s.can_edit }));
      toast.success("Funnel prospect salvato");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const doPreview = async (fid, si) => {
    try {
      const st = cfg.funnels[fid].steps[si];
      const r = await api.post("/prospect-funnel-config/anteprima", { fid, step_index: si, subject: st.subject, testo: st.testo });
      setPreview({ fid, si, ...r.data });
    } catch (e) { toast.error(e?.response?.data?.detail || "Anteprima non disponibile"); }
  };

  const sendTest = async (fid, si) => {
    try {
      const r = await api.post("/prospect-funnel-config/invia-prova", { fid, step_index: si });
      toast.success(`Email di prova inviata a ${r.data.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invio prova non riuscito"); }
  };

  return (
    <Card className="p-6" data-testid="prospect-funnel-config-panel">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 text-left" data-testid="pfunnel-toggle">
          {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          <Filter className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          <h2 className="font-heading text-lg font-bold tracking-tight text-emerald-700 dark:text-emerald-400">Funnel email prospect — sequenze automatiche</h2>
        </button>
        <Badge variant={cfg.attivo_globale ? "default" : "outline"} className="text-[10px]">{cfg.attivo_globale ? "MOTORE ATTIVO" : "MOTORE SPENTO"}</Badge>
        <label className="ml-auto flex cursor-pointer items-center gap-2 text-sm font-semibold">
          <Checkbox checked={cfg.attivo_globale} onCheckedChange={(v) => setCfg((s) => ({ ...s, attivo_globale: !!v }))} data-testid="pfunnel-global" /> Attiva motore globale
        </label>
      </div>

      {open && (<>
        <p className="mb-4 mt-4 text-xs text-muted-foreground">
          Due sequenze email automatiche per i prospect. Un solo funnel attivo per prospect. Regole: <b>risposta/nuovo appuntamento → pausa</b>; <b>diventa partner/ordine/perso → termina</b>. Segnaposto disponibili: {(cfg._placeholders || []).map((p) => <code key={p} className="mx-0.5">{p}</code>)}.
        </p>

        <label className="mb-2 flex w-fit cursor-pointer items-center gap-2 text-sm font-medium">
          <Checkbox checked={cfg.cc_agente !== false} onCheckedChange={(v) => setCfg((s) => ({ ...s, cc_agente: !!v }))} data-testid="pfunnel-cc-agente" />
          Metti in copia (CC) l'agente incaricato dell'anagrafica — è lo stesso mittente
        </label>

        <div className="mb-4 flex flex-col gap-2">
          <label className="flex w-fit cursor-pointer items-center gap-2 text-sm font-medium">
            <Checkbox checked={!!cfg.cc_extra_enabled} onCheckedChange={(v) => setCfg((s) => ({ ...s, cc_extra_enabled: !!v }))} data-testid="pfunnel-cc-extra-flag" />
            Metti in copia (CC) anche altri indirizzi
          </label>
          {cfg.cc_extra_enabled && (
            <Input
              value={cfg.cc_extra || ""}
              onChange={(e) => setCfg((s) => ({ ...s, cc_extra: e.target.value }))}
              placeholder="es. mario.rossi@torrisolare.it, direzione@torrisolare.it"
              className="max-w-xl text-sm"
              data-testid="pfunnel-cc-extra-input"
            />
          )}
          {cfg.cc_extra_enabled && <p className="text-[11px] text-muted-foreground">Uno o più indirizzi separati da virgola. Verranno messi in CC su ogni email del funnel.</p>}
        </div>

        {/* ===== Riepilogo funnel (sola lettura) ===== */}
        <div className="overflow-hidden rounded-md border border-border" data-testid="pfunnel-summary">
          <div className="grid grid-cols-12 gap-2 border-b border-border bg-muted/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <div className="col-span-4">Funnel</div>
            <div className="col-span-4">Descrizione</div>
            <div className="col-span-2 text-center">Iscritti</div>
            <div className="col-span-2 text-center">Contenuto</div>
          </div>
          {(cfg.order || []).map((fid) => {
            const f = cfg.funnels[fid];
            const n = iscritti[fid]?.count ?? 0;
            return (
              <div key={fid} className="grid grid-cols-12 items-center gap-2 border-b border-border px-3 py-2.5 text-sm last:border-b-0" data-testid={`pfunnel-row-${fid}`}>
                <div className="col-span-4">
                  <div className="flex items-center gap-2 font-semibold">
                    {fid} · {f.nome}
                    {!f.attivo && <Badge variant="outline" className="text-[9px]">disattivo</Badge>}
                  </div>
                  <div className="text-[11px] text-muted-foreground">{f.steps.length} email{f.continuativo ? " · continuativo" : ""}</div>
                </div>
                <div className="col-span-4 text-xs text-muted-foreground">{f.obiettivo}</div>
                <div className="col-span-2 text-center">
                  <Button size="sm" variant="outline" className="h-8" onClick={() => setIscrittiDlg(fid)} data-testid={`pfunnel-iscritti-${fid}`}>
                    <Users className="mr-1 h-4 w-4" /> {n}
                  </Button>
                </div>
                <div className="col-span-2 text-center">
                  <Button size="sm" variant="outline" className="h-8" onClick={() => setContenutoDlg(fid)} data-testid={`pfunnel-contenuto-${fid}`}>
                    <FileText className="mr-1 h-4 w-4" /> Anteprima
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* ===== Editor testi (a comparsa) ===== */}
        <div className="mt-4">
          <Button size="sm" variant={editMode ? "secondary" : "outline"} onClick={() => setEditMode((v) => !v)} data-testid="pfunnel-edit-toggle">
            <Pencil className="mr-2 h-4 w-4" /> {editMode ? "Nascondi modifica testi" : "Modifica testi email"}
          </Button>
        </div>

        {editMode && (
          <div className="mt-3 space-y-2">
            {(cfg.order || []).map((fid) => {
              const f = cfg.funnels[fid];
              const isOpen = expanded === fid;
              return (
                <div key={fid} className="rounded-md border border-border" data-testid={`pfunnel-${fid}`}>
                  <div className="flex items-center gap-2 p-3">
                    <button type="button" onClick={() => setExpanded(isOpen ? "" : fid)} className="flex flex-1 items-center gap-2 text-left" data-testid={`pfunnel-${fid}-toggle`}>
                      {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      <span className="font-semibold">{fid} · {f.nome}</span>
                      <span className="text-xs text-muted-foreground">{f.steps.length} email{f.continuativo ? " · continuativo" : ""}</span>
                    </button>
                    <label className="flex cursor-pointer items-center gap-1.5 text-xs"><Checkbox checked={f.attivo} onCheckedChange={(v) => setFunnel(fid, { attivo: !!v })} data-testid={`pfunnel-${fid}-attivo`} /> attivo</label>
                  </div>
                  {isOpen && (
                    <div className="space-y-4 border-t border-border p-3">
                      <p className="text-xs text-muted-foreground">{f.obiettivo}</p>
                      {f.steps.map((st, i) => (
                        <div key={i} className="rounded-md bg-muted/30 p-2.5" data-testid={`pfunnel-${fid}-step-${i}`}>
                          <div className="mb-1 flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px]">Giorno {st.day}</Badge>
                            <Input value={st.subject} onChange={(e) => setStep(fid, i, { subject: e.target.value })} placeholder="Oggetto" className="h-8 flex-1" data-testid={`pfunnel-${fid}-subject-${i}`} />
                            <Button size="sm" variant="ghost" className="h-8 px-2" title="Anteprima" onClick={() => doPreview(fid, i)} data-testid={`pfunnel-${fid}-preview-${i}`}><Eye className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" className="h-8 px-2 text-emerald-600 hover:text-emerald-700" title="Invia una prova a te stesso" onClick={() => sendTest(fid, i)} data-testid={`pfunnel-${fid}-test-${i}`}><Send className="h-4 w-4" /></Button>
                          </div>
                          <textarea value={st.testo} onChange={(e) => setStep(fid, i, { testo: e.target.value })} rows={6}
                            className="w-full rounded-md border border-border bg-background p-2 font-mono text-xs" data-testid={`pfunnel-${fid}-testo-${i}`} />
                          {preview && preview.fid === fid && preview.si === i && (
                            <div className="mt-2 rounded-md border border-border bg-background p-2" data-testid={`pfunnel-${fid}-preview-out-${i}`}>
                              <div className="mb-1 text-xs text-muted-foreground">Oggetto: <span className="font-semibold text-foreground">{preview.subject}</span></div>
                              <pre className="whitespace-pre-wrap font-sans text-sm">{preview.testo}</pre>
                            </div>
                          )}
                        </div>
                      ))}
                      {Object.keys(f.links || {}).length > 0 && (
                        <div className="rounded-md border border-dashed border-border p-2.5" data-testid={`pfunnel-${fid}-cta`}>
                          <div className="mb-2 text-xs font-semibold text-muted-foreground">Link dei pulsanti (CTA) — dove porta ogni bottone</div>
                          {Object.keys(f.links).map((lab, li) => (
                            <div key={lab} className="mb-1.5 flex items-center gap-2">
                              <span className="w-56 shrink-0 truncate font-mono text-xs" title={lab}>[{lab}]</span>
                              <Input value={f.links[lab]} onChange={(e) => setLink(fid, lab, e.target.value)} placeholder="https://…" className="h-8 flex-1" data-testid={`pfunnel-${fid}-link-${li}`} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="pt-1">
              <Button onClick={save} disabled={saving} data-testid="pfunnel-save">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Salva funnel prospect</Button>
            </div>
          </div>
        )}
      </>)}

      {/* ===== Popup iscritti ===== */}
      <Dialog open={!!iscrittiDlg} onOpenChange={(v) => !v && setIscrittiDlg(null)}>
        <DialogContent className="max-w-lg" data-testid="pfunnel-iscritti-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-emerald-600" /> Iscritti — {iscrittiDlg} · {iscrittiDlg && cfg.funnels[iscrittiDlg]?.nome}</DialogTitle>
            <DialogDescription>Prospect attualmente in questo funnel (attivi o in pausa).</DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] space-y-1.5 overflow-auto">
            {(iscritti[iscrittiDlg]?.items || []).length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Nessun iscritto in questo funnel.</p>}
            {(iscritti[iscrittiDlg]?.items || []).map((it) => (
              <Link key={it.id} to={`/prospect/${it.id}`} className="flex items-center gap-2 rounded-md border border-border p-2 text-sm transition-colors hover:bg-muted/50" data-testid={`pfunnel-iscritto-${it.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{it.name}</div>
                  <div className="text-[11px] text-muted-foreground">{it.city || "—"} · prossima email {it.stato === "in_pausa" ? "in pausa" : itDate(it.prossima_email_il)}</div>
                </div>
                <Badge className={`text-[10px] ${it.stato === "attivo" ? "border-emerald-300 bg-emerald-100 text-emerald-800" : "border-amber-300 bg-amber-100 text-amber-800"}`}>{it.stato === "attivo" ? "attivo" : "in pausa"}</Badge>
                <span className="text-[11px] text-muted-foreground">step {Math.min((it.step || 0) + 1, iscrittiDlg ? cfg.funnels[iscrittiDlg].steps.length : 1)}/{iscrittiDlg ? cfg.funnels[iscrittiDlg].steps.length : 1}</span>
                <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </Link>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Popup contenuto (anteprima testi, sola lettura) ===== */}
      <Dialog open={!!contenutoDlg} onOpenChange={(v) => !v && setContenutoDlg(null)}>
        <DialogContent className="max-w-2xl" data-testid="pfunnel-contenuto-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-emerald-600" /> Contenuto — {contenutoDlg} · {contenutoDlg && cfg.funnels[contenutoDlg]?.nome}</DialogTitle>
            <DialogDescription>Anteprima dei testi delle email (sola lettura). Segnaposto mostrati come nei template.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] space-y-4 overflow-auto">
            {contenutoDlg && cfg.funnels[contenutoDlg].steps.map((st, i) => (
              <div key={i} className="rounded-md border border-border p-3" data-testid={`pfunnel-contenuto-step-${i}`}>
                <div className="mb-2 flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">Giorno {st.day}</Badge>
                  <span className="font-semibold text-foreground">{st.subject}</span>
                </div>
                <pre className="whitespace-pre-wrap font-sans text-sm text-muted-foreground">{st.testo}</pre>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
